import React, { useState, useEffect } from 'react';
import './App.css';

// Components
import MapComponent from './components/MapComponent';
import ReviewForm from './components/ReviewForm';
import EnhancedReviewList from './components/EnhancedReviewList';
import ActiveUsersCounter from './components/ActiveUsersCounter';
import FeedbackButton from './components/FeedbackButton';
import TotalVisitsCounter from './components/TotalVisitsCounter';
import TotalReviewsCounter from './components/TotalReviewsCounter';
import FloatingSearch from './components/FloatingSearch';

// Services and utilities
import {
  addReview, getReviewsNearLocation, getClusteredReviewLocations, generateLocationSummary,
} from './services/reviewsService';
import { getCurrentPosition } from './utils/geolocation';
import { searchLocation, validateSearchResult } from './services/searchService';
import {
  initializeGA4, trackUserLocation, trackReviewSubmitted, trackClusterViewed, trackMapInteraction, initializeAnalytics, getGAMeasurementId,
} from './utils/analytics';

const App = () => {
  // State management
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReviewList, setShowReviewList] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  // Initialize app - get user location and sign in anonymously
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize GA4 with enhanced measurements
        console.log('App: GA Measurement ID:', getGAMeasurementId());
        initializeGA4();
        initializeAnalytics(true); // Enable analytics by default

        // Skip Firebase auth for now - app works without it
        console.log('App: Skipping Firebase auth for now');

        // Get user location
        const location = await getCurrentPosition();
        console.log('App: Got user location:', location);
        setUserLocation(location);

        // Track user location with enhanced GA4 data
        await trackUserLocation(location.lat, location.lng);

        // Track user location (privacy-preserving)
        trackUserLocation(location.lat, location.lng);
        trackMapInteraction('location_found', { success: true });

        // Load clustered review locations
        try {
          const clusteredLocations = await getClusteredReviewLocations();
          setClusters(clusteredLocations);
          console.log('Loaded clusters:', clusteredLocations.length);
        } catch (error) {
          console.warn('Could not load review clusters:', error);
          setClusters([]);
        }
      } catch (err) {
        console.error('Error initializing app:', err);
        setError(err.message);

        // Set default location (e.g., San Francisco) if geolocation fails
        const defaultLocation = { lat: 37.7749, lng: -122.4194 };
        setUserLocation(defaultLocation);

        try {
          const nearbyReviews = await getReviewsNearLocation(
            defaultLocation.lat,
            defaultLocation.lng,
            0.5,
          );
          setReviews(nearbyReviews);
        } catch (reviewErr) {
          console.error('Error loading reviews:', reviewErr);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Handle map click (called from popup button)
  const handleMapClick = async (location) => {
    setSelectedLocation(location);
    setShowReviewForm(true);
    setShowReviewList(false);
  };

  // Handle cluster click - show enhanced review list
  const handleClusterClick = async (cluster) => {
    setSelectedCluster(cluster);
    setShowReviewList(true);
    setShowReviewForm(false);

    // Track cluster viewing
    trackClusterViewed(cluster);
  };

  // Handle review submission
  const handleReviewSubmit = async (reviewData) => {
    setSubmittingReview(true);
    try {
      const newReview = await addReview(reviewData);

      // Refresh clusters to include the new review
      try {
        const updatedClusters = await getClusteredReviewLocations();
        setClusters(updatedClusters);
      } catch (error) {
        console.warn('Could not refresh clusters:', error);
      }

      // Close form and show success
      setShowReviewForm(false);
      setSelectedLocation(null);
      setSelectedCluster(null);
    } catch (err) {
      console.error('Error submitting review:', err);
      trackMapInteraction('review_submit_error', { error: err.message });
      alert('Error submitting review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }

    // Track successful review submission
    trackReviewSubmitted(reviewData);
  };

  // Handle closing forms/lists
  const handleCloseForms = () => {
    setShowReviewForm(false);
    setShowReviewList(false);
    setSelectedLocation(null);
    setSelectedCluster(null);
  };

  // Handle adding new review from review list
  const handleNewReview = () => {
    if (selectedCluster) {
      // Use the cluster center as the location for the new review
      setSelectedLocation(selectedCluster.center);
    }
    setShowReviewList(false);
    setShowReviewForm(true);
  };

  // Handle location search
  const handleLocationSearch = async (searchQuery) => {
    try {
      console.log('Searching for location:', searchQuery);

      const searchResult = await searchLocation(searchQuery);

      if (!validateSearchResult(searchResult)) {
        throw new Error('Invalid search result');
      }

      console.log('Search result:', searchResult);

      // Update user location to search result
      const newLocation = {
        lat: searchResult.latitude,
        lng: searchResult.longitude,
        address: searchResult.placeName,
        searchQuery: searchQuery
      };

      setUserLocation(newLocation);

      // Load reviews near the searched location
      const nearbyClusters = await getClusteredReviewLocations(
        searchResult.latitude,
        searchResult.longitude,
        0.5 // 500m radius
      );
      setClusters(nearbyClusters);

      // Track the search
      trackMapInteraction('search', {
        query: searchQuery,
        result: searchResult.placeName,
        latitude: searchResult.latitude,
        longitude: searchResult.longitude
      });

      // Show success message
      alert(`Found: ${searchResult.placeName}\n\nLoaded ${nearbyClusters.length} buildings with reviews in this area.`);

    } catch (error) {
      console.error('Search failed:', error);
      alert(`Search failed: ${error.message}\n\nTry a different address or postal code.`);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
        <p>Loading RentVibe...</p>
        <p className="loading-subtitle">Getting your location and loading nearby reviews</p>
      </div>
    );
  }

  if (error && !userLocation) {
    return (
      <div className="app-error">
        <h2>Unable to load RentVibe</h2>
        <p>{error}</p>
        <p>Please check your location permissions and try refreshing the page.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>RentVibe</h1>
          <p>Discover honest rental reviews from real people</p>
        </div>
        <div className="header-actions">
          <div className="header-stats-section">
            <div className="header-stats">
              <span>
                🏢 {clusters.length} buildings, 📝 {reviews.length} reviews nearby
              </span>
            </div>
          </div>
          <div className="header-counters">
            <FeedbackButton />
            <ActiveUsersCounter />
            <TotalVisitsCounter />
            <TotalReviewsCounter />
          </div>
        </div>
      </header>

      <main className="app-main">
        {userLocation && (
          <MapComponent
            center={userLocation}
            clusters={clusters}
            onMapClick={handleMapClick}
            onClusterClick={handleClusterClick}
          />
        )}
        {!userLocation && !loading && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>No location available. Please refresh and allow location access.</p>
          </div>
        )}

        {showReviewForm && selectedLocation && (
          <div className="modal-overlay">
            <ReviewForm
              onSubmit={handleReviewSubmit}
              onCancel={handleCloseForms}
              initialLocation={selectedLocation}
              isLoading={submittingReview}
            />
          </div>
        )}

        {showReviewList && selectedCluster && (
          <EnhancedReviewList
            cluster={selectedCluster}
            onClose={handleCloseForms}
            onNewReview={handleNewReview}
            onGenerateSummary={generateLocationSummary}
          />
        )}

      </main>

      <footer className="app-footer">
        <p>© 2025 RentVibe - Anonymous rental reviews to help you find the perfect home</p>
      </footer>

      {/* Floating Search Button */}
      <FloatingSearch onSearch={handleLocationSearch} />
    </div>
  );
};

export default App;
